import { setlist } from '@/utils/typesense';
import {
  EXCLUDE_LUTS_FILTER,
  LUTS_FILTER,
  MOTION_GRAPHICS_CATEGORIES,
  MOTION_GRAPHICS_COLLECTION,
  STOCK_VIDEO_CATEGORIES,
  STOCK_VIDEO_COLLECTION,
} from '@music-vine/setlist';
import { LUTS_MG_COLLECTION_MARKER } from '@/features/GlobalSearch/utils/mergeCollections';
import { mapAssetTypeToSearchTypeParam } from '@/features/GlobalSearch/utils/mapAssetTypeToSearchTypeParam';
import { buildSearchUrl } from '@/features/SearchPage/utils/searchUrl';
import type { AssetType } from '@/features/GlobalSearch/types/index';

import type { GenericSearchResult } from '@/features/SearchPage/types';
import type {
  SearchResponse,
  TypesenseMultiSearchResponse,
} from '@music-vine/setlist';

// Index of the LUTs assets search in the multi_search request. We rely on
// order rather than `filter_by` inspection because Typesense doesn't
// guarantee round-tripping every request param.
const LUTS_ASSETS_SEARCH_INDEX = 9;

export const getTrendingSearches = async () => {
  const params = {
    searches: [
      {
        q: '*',
        collection: 'trackqualifiedkeywords',
        query_by: 'name',
        per_page: 6,
        sort_by: 'weighted_value:desc',
      },
      {
        q: '*',
        collection: 'sfxqualifiedkeywords',
        query_by: 'name',
        per_page: 10,
        sort_by: 'weighted_value:desc',
      },
      {
        q: '*',
        collection: 'collections',
        query_by: 'name',
        per_page: 8,
        sort_by: 'weighted_value:desc',
      },
      {
        q: '*',
        collection: 'sfxcollections',
        query_by: 'name',
        per_page: 8,
        sort_by: 'weighted_value:desc',
      },
      {
        q: '*',
        collection: 'artists',
        query_by: 'name',
        per_page: 4,
        sort_by: 'weighted_value:desc',
      },
      {
        q: '*',
        collection: MOTION_GRAPHICS_CATEGORIES,
        query_by: 'name',
        per_page: 8,
        filter_by:
          'relative_url:=[/motion-graphics/category/buttons/social-media/subscribe-buttons, /motion-graphics/category/titles, /motion-graphics/category/transitions, /motion-graphics/category/overlays, /motion-graphics/category/backgrounds, /motion-graphics/category/intros]',
        exhaustive_search: true,
      },
      {
        q: '*',
        collection: MOTION_GRAPHICS_COLLECTION,
        query_by: 'name',
        per_page: 8,
        filter_by: EXCLUDE_LUTS_FILTER,
        sort_by: 'premium_download_count:desc',
      },
      {
        q: '*',
        collection: STOCK_VIDEO_CATEGORIES,
        query_by: 'name',
        filter_by: 'id:=[4, 953, 570, 1125, 985, 414, 1459, 332]',
        per_page: 8,
      },
      {
        q: '*',
        collection: STOCK_VIDEO_COLLECTION,
        query_by: 'title',
        per_page: 8,
      },
      {
        q: '*',
        collection: MOTION_GRAPHICS_COLLECTION,
        query_by: 'name',
        per_page: 8,
        filter_by: LUTS_FILTER,
        sort_by: 'premium_download_count:desc',
      },
    ],
  };

  const search = (await setlist.multiSearch(
    params
  )) as TypesenseMultiSearchResponse<GenericSearchResult>;

  // Retag the LUTs assets response before we drop empty results — the empty
  // filter would otherwise strand it under the motion label if the collection
  // ordering changed.
  const lutsResult = search?.results?.[LUTS_ASSETS_SEARCH_INDEX];
  if (lutsResult?.request_params) {
    lutsResult.request_params.collection_name = LUTS_MG_COLLECTION_MARKER;
  }

  const clearedList = search?.results?.filter(
    (result: SearchResponse<GenericSearchResult>) =>
      result.hits?.length && result.hits?.length > 0
  );

  return {
    search: clearedList,
  };
};

export const getTrendingResultUrl = ({
  name,
  type,
}: {
  name: string;
  type: AssetType;
}) =>
  buildSearchUrl(
    mapAssetTypeToSearchTypeParam(type),
    (name ?? '').toLowerCase()
  );
